import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Lock, LockOpen, Package, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const eur = (v) => (v || 0).toLocaleString(undefined, { style: "currency", currency: "EUR" });
const blank = { name: "", buyer: "", sale_date: "", notes: "" };

export default function Sales() {
  const { user } = useAuth();
  const isOwner = user.role === "owner";
  const [sales, setSales] = useState([]);
  const [f, setF] = useState(blank);
  const load = () => api.get("/sales").then((r) => setSales(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (await run(() => api.post("/sales", f), "Sale created")) setF(blank);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Vendas</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] mb-3">Sales</h2>
      <p className="text-[#A89F91] mb-8 text-sm">Create a sale here, then on the Scan page choose "Sale", pick it and type the sale box number. Closed sales no longer appear when scanning.</p>
      {isOwner && (
        <form onSubmit={add} className="grid sm:grid-cols-[1.4fr_1fr_160px_auto] gap-3 mb-8">
          <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Sale name, e.g. Order 2026-014" className="h-11 bg-[#161214]" data-testid="sale-name-input" />
          <Input value={f.buyer} onChange={(e) => setF({ ...f, buyer: e.target.value })} placeholder="Buyer (optional)" className="h-11 bg-[#161214]" data-testid="sale-buyer-input" />
          <Input type="date" value={f.sale_date} onChange={(e) => setF({ ...f, sale_date: e.target.value })} className="h-11 bg-[#161214]" data-testid="sale-date-input" />
          <Button type="submit" className="h-11 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="add-sale-button">Create sale</Button>
        </form>
      )}
      <div className="space-y-3" data-testid="sales-list">
        {sales.map((s) => <SaleRow key={s.id} sale={s} isOwner={isOwner} run={run} />)}
        {!sales.length && <p className="text-center text-[#6E665D] py-10" data-testid="sales-empty">No sales yet.</p>}
      </div>
    </div>
  );
}

const SaleRow = ({ sale, isOwner, run }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const toggle = async () => {
    if (!open) {
      try {
        const { data } = await api.get(`/sales/${sale.id}`);
        setDetail(data);
      } catch (e) {
        return toast.error(errMsg(e));
      }
    }
    setOpen(!open);
  };
  const { id, bottles, total, boxes, ...fields } = sale;
  return (
    <div className={`rounded-xl border bg-[#161214] ${sale.closed ? "border-[#2E252A] opacity-70" : "border-[#4A3B43]"}`} data-testid={`sale-row-${sale.id}`}>
      <div className="flex items-center gap-4 px-5 py-4">
        <button onClick={toggle} className="flex-1 text-left" data-testid={`sale-open-${sale.id}`}>
          <p className="text-[#F5F0EB] flex items-center gap-2">{sale.name}{sale.closed && <span className="font-mono text-[10px] uppercase tracking-widest text-[#6E665D]">closed</span>}<ChevronDown className={`w-4 h-4 text-[#6E665D] transition-transform ${open ? "rotate-180" : ""}`} /></p>
          <p className="text-xs text-[#6E665D]">{[sale.buyer, sale.sale_date].filter(Boolean).join(" · ")}</p>
        </button>
        <div className="text-right font-mono text-xs">
          <p className="text-[#D4AF37]" data-testid={`sale-count-${sale.id}`}>{bottles} btl · {eur(total)}</p>
          {!!boxes.length && <p className="text-[#6E665D]">box {boxes.join(", ")}</p>}
        </div>
        {isOwner && (
          <>
            <button onClick={() => run(() => api.put(`/sales/${sale.id}`, { ...fields, closed: !sale.closed }), sale.closed ? "Sale reopened" : "Sale closed")} className="text-[#A89F91] hover:text-[#D4AF37]" data-testid={`sale-close-${sale.id}`} aria-label={sale.closed ? "Reopen" : "Close"}>
              {sale.closed ? <LockOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
            </button>
            <button onClick={() => window.confirm(`Delete ${sale.name}?`) && run(() => api.delete(`/sales/${sale.id}`), "Sale deleted")} className="text-[#A89F91] hover:text-[#8C2B36]" data-testid={`sale-delete-${sale.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
          </>
        )}
      </div>
      {open && detail && (
        <div className="border-t border-[#2E252A] px-5 py-4 space-y-4" data-testid={`sale-detail-${sale.id}`}>
          {detail.boxes.map((b) => (
            <div key={b.sale_box_number || "none"}>
              <p className="flex items-center gap-2 text-sm text-[#D4AF37] mb-1.5"><Package className="w-4 h-4" strokeWidth={1.5} />{b.sale_box_number ? `Sale box ${b.sale_box_number}` : "No sale box"} · {b.bottles.length} btl</p>
              {b.bottles.map((x) => (
                <p key={x.barcode} className="text-sm text-[#F5F0EB] pl-6"><span className="font-mono text-xs text-[#A89F91] mr-3">{x.barcode}</span>{x.label}{x.sale_price != null ? ` · ${eur(x.sale_price)}` : ""}</p>
              ))}
            </div>
          ))}
          {!detail.boxes.length && <p className="text-sm text-[#6E665D]">No bottles in this sale yet.</p>}
        </div>
      )}
    </div>
  );
};
